import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import { getAsYouType } from 'awesome-phonenumber';
import Button from '../Button';
import InternalLink from '../InternalLink';
import LabeledCheckbox from '../LabeledCheckbox';
import LabeledCombobox from '../LabeledCombobox';
import LabeledDropdown from '../LabeledDropdown';
import LabeledTextbox from '../LabeledTextbox';
import { SelectOption } from '../../../types/common';
import { AddressField, AddressFormData } from '../../../types/Address';
import { addressSchema } from '../../../models/schemas/Address';
import {
  AddressAutocompleteDetailsContract,
  AddressAutocompleteMatch,
} from '../../../types/AddressValidation';
import AutocompleteMatchList from '../AutocompleteMatchList';

export const AddressFormWithButtonsTestId = 'address-form-with-buttons-input';
export const AddressFormSubmitButtonTestId = 'address-form-submit-button';
export const AddressFormCancelButtonTestId = 'address-form-cancel-button';
export const AddressFormMissingAddress2TestId = 'address-form-missing-address2';

type CancelableFormProps =
  | {
      displayCancelButton: true;
      onCancelButtonClicked: () => void;
      cancelButtonText?: string;
    }
  | {
      displayCancelButton?: never;
      onCancelButtonClicked?: never;
      cancelButtonText?: never;
    };

export type FormWithButtonsProps<FormPropsType> = {
  defaultValues?: FormPropsType;
  submitButtonText?: string;
  isSaving?: boolean;
  onSubmitClicked: (formData: FormPropsType) => Promise<void>;
  onErrorStatus?: (hasError: boolean) => void;
} & CancelableFormProps;

type AddressAutocompleteProps = {
  matches: AddressAutocompleteMatch[];
  onAutocompleteData: (address1: string) => Promise<void>;
  onAutocompleteSelect: (
    placeId: string
  ) => Promise<AddressAutocompleteDetailsContract | null>;
  onAutocompleteClear: () => void;
};

export type AddressFormWithButtonsProps =
  FormWithButtonsProps<AddressFormData> & {
    countryOptions: SelectOption[];
    getStateConfig: (country: string) => {
      options: SelectOption[];
      label: string;
    };
    isShippingAddress?: boolean;
    autoFocus?: boolean;
    makeNonMissingFieldsEditable?: boolean;
    missingFields?: AddressField[];
    missingAddress2?: boolean;
    autocompleteProps?: AddressAutocompleteProps;
    shippingRatesAddressType?: string | null;
  };

const AddressFormWithButtons: React.FC<AddressFormWithButtonsProps> = ({
  countryOptions,
  getStateConfig,
  defaultValues,
  displayCancelButton,
  submitButtonText = 'Save',
  cancelButtonText = 'Cancel',
  autoFocus = false,
  isSaving = false,
  isShippingAddress = false,
  makeNonMissingFieldsEditable = false,
  missingFields = [],
  autocompleteProps,
  onSubmitClicked,
  onErrorStatus,
  onCancelButtonClicked,
  missingAddress2 = false,
  shippingRatesAddressType,
}: AddressFormWithButtonsProps) => {
  const { options: initialStateOptions, label: initialStateLabel } =
    getStateConfig(defaultValues?.country ?? countryOptions[0].value);

  const [stateOptions, setStateOptions] =
    useState<SelectOption[]>(initialStateOptions);
  const [stateLabel, setStateLabel] = useState<string>(initialStateLabel);
  const [stateDefaultValue, setStateDefaultValue] = useState<
    string | undefined
  >(defaultValues?.state);
  const [isAddress1Focused, setIsAddress1Focused] = useState(false);
  const [ignoreAddress1Input, setIgnoreAddress1Input] = useState(true);
  const [isAutocompleteAddress, setIsAutocompleteAddress] = useState(false);
  const [disableStatePlaceholder, setDisableStatePlaceholder] = useState(false);
  const [countryCode, setCountryCode] = useState('');

  const {
    formState: { errors, isValid },
    getValues,
    register,
    trigger,
    watch,
    setValue,
    control,
    clearErrors,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(addressSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!isValid && Object.keys(errors).length > 0) {
      onErrorStatus?.(true);
    } else {
      onErrorStatus?.(false);
    }
  }, [onErrorStatus, isValid, errors]);

  const watchCountry = watch(
    'country',
    defaultValues?.country ?? countryOptions[0]?.value
  );

  useEffect(() => {
    const { options, label } = getStateConfig(watchCountry ?? '');
    setStateOptions(options);
    setStateLabel(label);

    clearErrors('state');
    if (watchCountry === defaultValues?.country && !!defaultValues?.state) {
      setValue('state', defaultValues.state);
      setDisableStatePlaceholder(true);
    } else if (options.length) {
      setValue('state', options[0].value);
      setDisableStatePlaceholder(false);
    } else {
      setValue('state', undefined);
      setDisableStatePlaceholder(false);
    }
    // eslint-disable-next-line
  }, [watchCountry]);

  const shouldAutoFocusField = useCallback(
    (fieldName: AddressField) => {
      if (!autoFocus) {
        return false;
      }
      if (missingFields.length === 0) {
        return fieldName === 'firstName';
      }
      if (missingFields.length > 0) {
        return fieldName === missingFields[0];
      }
      return false;
    },
    [autoFocus, missingFields]
  );

  const isFieldReadOnly = useCallback(
    (fieldName: AddressField) => {
      return (
        !makeNonMissingFieldsEditable &&
        missingFields.length > 0 &&
        !missingFields.includes(fieldName)
      );
    },
    [makeNonMissingFieldsEditable, missingFields]
  );

  const handleInputOnFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    if (e.target.name !== 'address1') {
      autocompleteProps?.onAutocompleteClear();
      setIsAddress1Focused(false);
      setIgnoreAddress1Input(true);
    } else {
      setIsAddress1Focused(true);
    }
  };

  const handleAutocompleteSelection = async (
    placeId: string
  ): Promise<void> => {
    if (autocompleteProps) {
      const address = await autocompleteProps.onAutocompleteSelect(placeId);
      setValue('country', address?.country);
      setValue('address1', address?.address1);
      setValue('city', address?.city);
      setStateDefaultValue(address?.state);
      setValue('state', address?.state);
      setValue('postalCode', address?.postalCode);
      setIsAutocompleteAddress(true);
      autocompleteProps.onAutocompleteClear();
      await trigger();
    }
  };

  const handleSubmitClicked = async (): Promise<void> => {
    await trigger();

    if (Object.keys(errors).length === 0 && isValid) {
      onErrorStatus?.(false);
      await onSubmitClicked(getValues() as AddressFormData);
    } else {
      onErrorStatus?.(true);
    }
  };
  useEffect(() => {
    if (shippingRatesAddressType === 'residential') {
      setValue('residential', true);
    } else if (shippingRatesAddressType === 'business') {
      setValue('residential', false);
    }
  }, [shippingRatesAddressType, setValue]);

  const getCountryCode = async () => {
    try {
      const response = await axios.get('https://ipapi.co/json/');

      if (response?.data?.country_code) {
        setCountryCode(response.data.country_code);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching the country code:', error);
    }
  };

  useEffect(() => {
    getCountryCode();
  }, []);

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    const formattedNumber = getAsYouType(countryCode || 'US');
    formattedNumber.reset(input.replace(/[^+0-9]/g, ''));

    if (formattedNumber) {
      return formattedNumber?.getPhoneNumber()?.number?.input || input;
    }
    return '';
  };

  return (
    <div className="px-2 flex flex-col pb-4">
      <div>
        <form data-testid={AddressFormWithButtonsTestId}>
          {(process.env.REACT_APP_ENV === 'QA' ||
            process.env.NODE_ENV === 'development') && (
            <DevTool control={control} placement="top-right" />
          )}
          <div className="flex flex-col md:flex-row md:justify-items-stretch w-full">
            <LabeledTextbox
              name="firstName"
              label="First name"
              autoComplete={isFieldReadOnly('firstName') ? 'new' : 'given-name'}
              className="mr-2.5 mb-0 md:w-1/2"
              errorLabel={errors?.firstName?.message}
              defaultValue={defaultValues?.firstName}
              register={register}
              autoFocus={shouldAutoFocusField('firstName')}
              readOnly={isFieldReadOnly('firstName')}
              onFocus={handleInputOnFocus}
            />
            <LabeledTextbox
              name="lastName"
              label="Last name"
              autoComplete={isFieldReadOnly('lastName') ? 'new' : 'family-name'}
              className="ml-2.5 mb-0 md:w-1/2"
              errorLabel={errors?.lastName?.message}
              defaultValue={defaultValues?.lastName}
              register={register}
              autoFocus={shouldAutoFocusField('lastName')}
              readOnly={isFieldReadOnly('lastName')}
              onFocus={handleInputOnFocus}
            />
          </div>
          <div className="flex flex-col justify-items-stretch w-full">
            <LabeledTextbox
              className="mb-0"
              name="companyName"
              label="Company (optional)"
              autoComplete="organization"
              errorLabel={errors?.companyName?.message}
              defaultValue={defaultValues?.companyName}
              register={register}
              onFocus={handleInputOnFocus}
            />
            <div className="flex flex-col">
              <LabeledTextbox
                className="mb-0"
                name="address1"
                label="Address"
                autoComplete={
                  autocompleteProps &&
                  isAddress1Focused &&
                  isFieldReadOnly('address1')
                    ? 'new'
                    : 'address-line1'
                }
                errorLabel={errors?.address1?.message}
                defaultValue={defaultValues?.address1}
                register={register}
                autoFocus={shouldAutoFocusField('address1')}
                readOnly={isFieldReadOnly('address1')}
                onFocus={handleInputOnFocus}
              />
              {autocompleteProps &&
                !ignoreAddress1Input &&
                Boolean(autocompleteProps.matches.length) && (
                  <div className="relative mx-2 -mt-1">
                    <div className="absolute z-10 border border-gray-500 rounded-b-md shadow-md w-full">
                      <AutocompleteMatchList
                        matches={autocompleteProps.matches}
                        onMatchSelect={handleAutocompleteSelection}
                      />
                    </div>
                  </div>
                )}
            </div>
            <LabeledTextbox
              className="mb-0"
              name="address2"
              label="Apartment, Building, Floor (optional)"
              autoComplete={
                isFieldReadOnly('address2') ? 'new' : 'address-line2'
              }
              errorLabel={errors?.address2?.message}
              defaultValue={defaultValues?.address2}
              register={register}
              autoFocus={shouldAutoFocusField('address2')}
              readOnly={isFieldReadOnly('address2')}
              onFocus={handleInputOnFocus}
            />
            {missingAddress2 && (
              <div
                className="customer-login-prompt text-xs text-center m-2 mb-0 p-2"
                data-testid={AddressFormMissingAddress2TestId}
              >
                This address usually has an apartment, building, or floor. To
                skip this, press continue below.
              </div>
            )}
          </div>
          <div className="flex flex-col md:flex-row md:justify-items-stretch w-full">
            <LabeledTextbox
              name="city"
              label="City"
              autoComplete={isFieldReadOnly('city') ? 'new' : 'address-level2'}
              className="mr-2.5 mb-0 md:w-1/2"
              errorLabel={errors?.city?.message}
              defaultValue={defaultValues?.city}
              register={register}
              autoFocus={shouldAutoFocusField('city')}
              readOnly={isFieldReadOnly('city')}
              onFocus={handleInputOnFocus}
              disablePlaceholder={isAutocompleteAddress}
            />
            <LabeledDropdown
              name="country"
              label="Country"
              autoComplete={isFieldReadOnly('country') ? 'new' : 'country'}
              className="ml-2.5 md:w-1/2"
              errorLabel={errors?.country?.message}
              defaultValue={defaultValues?.country}
              options={countryOptions}
              register={register}
              readOnly={isFieldReadOnly('country')}
              onFocus={handleInputOnFocus}
            />
          </div>

          <div className="flex flex-col md:flex-row md:justify-items-stretch w-full">
            <LabeledTextbox
              name="postalCode"
              label="ZIP code"
              autoComplete={
                isFieldReadOnly('postalCode') ? 'new' : 'postal-code'
              }
              className="mr-2.5 md:w-1/2"
              errorLabel={errors?.postalCode?.message}
              defaultValue={defaultValues?.postalCode}
              register={register}
              autoFocus={shouldAutoFocusField('postalCode')}
              readOnly={isFieldReadOnly('postalCode')}
              onFocus={handleInputOnFocus}
              disablePlaceholder={isAutocompleteAddress}
            />
            <LabeledCombobox
              name="state"
              label={stateLabel}
              autoComplete={isFieldReadOnly('state') ? 'new' : 'address-level1'}
              className="ml-2.5 mb-0 md:w-1/2"
              errorLabel={errors?.state?.message}
              defaultValue={stateDefaultValue}
              options={stateOptions}
              register={register}
              onFocus={handleInputOnFocus}
              readOnly={
                stateOptions && stateOptions.length
                  ? isFieldReadOnly('state')
                  : false
              }
              disablePlaceholder={
                isAutocompleteAddress || disableStatePlaceholder
              }
            />
          </div>
          {isShippingAddress && shippingRatesAddressType === null && (
            <LabeledCheckbox
              className="mb-4"
              name="residential"
              label="Residential Address"
              defaultChecked={defaultValues?.residential ?? true}
              register={register}
              onFocus={handleInputOnFocus}
            />
          )}
          <LabeledTextbox
            name="phoneNumber"
            label="Phone"
            type="tel"
            autoComplete={isFieldReadOnly('phoneNumber') ? 'new' : 'tel'}
            maxLength={30}
            pattern="^[\d\s\+\(\)\-\*\.#extn]{7,31}"
            errorLabel={errors?.phoneNumber?.message}
            defaultValue={defaultValues?.phoneNumber}
            register={register}
            autoFocus={shouldAutoFocusField('phoneNumber')}
            readOnly={isFieldReadOnly('phoneNumber')}
            onFocus={handleInputOnFocus}
            handlePhoneInputChange={handlePhoneInputChange}
          />
        </form>
      </div>
      <div className="flex flex-row justify-between justify-items-center">
        <div className={`${displayCancelButton ? 'w-3/4' : 'w-full'}`}>
          <Button
            center={!displayCancelButton}
            className={displayCancelButton ? '' : 'max-w-sm'}
            testId={AddressFormSubmitButtonTestId}
            isOtherActionPending={isSaving}
            onClick={handleSubmitClicked}
          >
            {submitButtonText}
          </Button>
        </div>
        {displayCancelButton && (
          <div className="self-center pr-2">
            <InternalLink
              testId={AddressFormCancelButtonTestId}
              onClick={(): void => onCancelButtonClicked?.()}
            >
              {cancelButtonText}
            </InternalLink>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressFormWithButtons;
